import { type FC, type PropsWithChildren } from 'react'
import { CloseButton, Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { Button } from '../../../../../components/Button/Button'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { NotificationChannel, type ReminderSettings } from '../../../../../models/Scheduling'
import { TemplateButton } from '../EmailTemplatesSetup/TemplateButton'
import {
  createRowDraft,
  formatReminderOffset,
  hasRowWithoutChannel,
  hasSmsEntry,
  MAX_REMINDER_ENTRIES,
  nextReminderOffset,
  type ReminderRowDraft,
  type ReminderSettingsDraft,
  settingsChanged,
} from '../reminderDrafts'
import { type SmsChannelState } from '../ReminderSettingsCard'
import { SettingsCard } from '../SettingsSection'
import { hasTemplateChange } from './reminderTemplates'
import styles from './Reminders.module.scss'

const TEMPLATE_LIBRARY_PATH = '/workspace/setup/templates?category=scheduling-emails-clients'
const NEW_ROW_CAVEAT = "New steps only apply to send times that haven't passed yet."
const LINK_CLASS = 'd-inline p-0 align-baseline zocks-text-primary text-decoration-underline'

export interface ReminderCardCopy {
  title: string
  heading: string
  description: string
  noun: string
  banner?: string
  trailingCopy: string
  addLabel: string
  removeLabel: string
  removeLastTooltip: string
}

export interface ReminderCardTemplates {
  nameFor: (row: { id: string | null; templateId?: string }) => string
  canChoose: (row: ReminderRowDraft) => boolean
  isLocked: (row: ReminderRowDraft) => boolean
  onChoose: (row: ReminderRowDraft) => void
}

export interface ReminderCardProps {
  copy: ReminderCardCopy
  settings: ReminderSettings
  draft: ReminderSettingsDraft | null
  smsChannel: SmsChannelState
  readOnly: boolean
  editDisabled: boolean
  editDisabledTooltip: string
  isSaving: boolean
  error: string | null
  templates: ReminderCardTemplates
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  onDraftChange: (draft: ReminderSettingsDraft) => void
  onShowTextMessages: () => void
}

const Hint: FC<PropsWithChildren<{ icon: string; className?: string }>> = ({ icon, className, children }) => (
  <span className={classNames('d-flex align-items-center gap-2 paragraph-small', className)}>
    <i aria-hidden='true' className={classNames('bi flex-shrink-0', icon)} />
    <span>{children}</span>
  </span>
)

const SMS_ONLY_HINT = "Clients who haven't opted in to texts won't get this reminder."

interface ReadOnlyRowsProps {
  copy: ReminderCardCopy
  settings: ReminderSettings
  showTextColumn: boolean
  textsOff: boolean
  templates: ReminderCardTemplates
}

const ReadOnlyRows: FC<ReadOnlyRowsProps> = ({ copy, settings, showTextColumn, textsOff, templates }) => (
  <div className='d-flex flex-column border rounded-2 zocks-border-color-gray-300'>
    {settings.entries.map((entry, index) => {
      const hasEmail = entry.channels.includes(NotificationChannel.EMAIL)
      const hasText = entry.channels.includes(NotificationChannel.SMS)
      return (
        <div key={entry.id ?? entry.offsetMinutes} className={classNames(styles.row, 'd-flex flex-column gap-1 px-3 py-2', { 'border-top': index > 0 })}>
          <div className={classNames(styles.line, 'd-flex align-items-center gap-3')}>
            <span className={styles.dot} />
            <span className='col-4'>
              <span className='bold'>{formatReminderOffset(entry.offsetMinutes)}</span> {copy.trailingCopy}
            </span>
            <span className={classNames('d-flex align-items-center gap-2 flex-grow-1', hasEmail ? 'fw-semibold' : 'zocks-text-gray-500')}>
              <i aria-hidden='true' className='bi bi-envelope' />
              {hasEmail ? templates.nameFor(entry) : 'No email'}
            </span>
            {showTextColumn ? (
              <span className={classNames('col-3 d-flex align-items-center gap-2', hasText ? 'fw-semibold' : 'zocks-text-gray-500')}>
                <i aria-hidden='true' className='bi bi-chat-left' />
                {hasText ? 'Text message' : 'No text message'}
              </span>
            ) : null}
          </div>
          {hasText && !hasEmail && !textsOff ? (
            <Hint icon='bi-info-circle' className={classNames(styles.channelsIndent, 'zocks-text-gray-900')}>
              {SMS_ONLY_HINT}
            </Hint>
          ) : null}
          {hasText && textsOff ? (
            <Hint icon='bi-exclamation-circle' className={classNames(styles.channelsIndent, 'zocks-text-gray-900')}>
              {hasEmail ? 'Only the email goes out.' : "This reminder doesn't go out."}
            </Hint>
          ) : null}
        </div>
      )
    })}
  </div>
)

interface RowTemplateProps {
  row: ReminderRowDraft
  templates: ReminderCardTemplates
}

const RowTemplate: FC<RowTemplateProps> = ({ row, templates }) => (
  <span className={classNames(styles.rowTemplate, 'd-flex align-items-center zocks-gap-0.625')}>
    <span className='border-start zocks-border-color-gray-300 align-self-stretch' />
    <span className='paragraph-small zocks-text-gray-600 text-nowrap'>Email template</span>
    <span className='d-flex flex-column align-items-start zocks-gap-0.25 mw-0'>
      <TemplateButton truncate name={templates.nameFor(row)} disabled={!templates.canChoose(row)} onClick={() => templates.onChoose(row)} />
      {templates.isLocked(row) ? <span className='paragraph-extra-small zocks-text-gray-600'>Managed by your enterprise</span> : null}
    </span>
  </span>
)

interface EditableRowsProps {
  copy: ReminderCardCopy
  settings: ReminderSettings
  draft: ReminderSettingsDraft
  smsChannel: SmsChannelState
  templates: ReminderCardTemplates
  onDraftChange: (draft: ReminderSettingsDraft) => void
}

const EditableRows: FC<EditableRowsProps> = ({ copy, settings, draft, smsChannel, templates, onDraftChange }) => {
  const showChannels = smsChannel !== 'hidden'
  const usedOffsets = draft.rows.map((row) => row.offsetMinutes)
  const removeDisabled = draft.rows.length === 1
  const updateRow = (key: string, change: Partial<Pick<ReminderRowDraft, 'offsetMinutes' | 'channels'>>) =>
    onDraftChange({ ...draft, rows: draft.rows.map((row) => (row.key === key ? { ...row, ...change } : row)) })
  const toggleChannel = (row: ReminderRowDraft, channel: NotificationChannel, checked: boolean) =>
    updateRow(row.key, {
      channels: checked
        ? [NotificationChannel.EMAIL, NotificationChannel.SMS].filter((candidate) => candidate === channel || row.channels.includes(candidate))
        : row.channels.filter((candidate) => candidate !== channel),
    })
  const removeRow = (key: string) => onDraftChange({ ...draft, rows: draft.rows.filter((row) => row.key !== key) })
  const addRow = () => onDraftChange({ ...draft, rows: [...draft.rows, createRowDraft(nextReminderOffset(settings.allowedOffsets, draft.rows))] })

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex flex-column border rounded-2 zocks-border-color-gray-300 overflow-hidden'>
        {draft.rows.map((row, index) => {
          const hasEmail = row.channels.includes(NotificationChannel.EMAIL)
          const noChannel = row.channels.length === 0
          const offsetSelect = (
            <span className='d-flex align-items-center gap-2 flex-shrink-0'>
              <Form.Select
                className='w-auto'
                aria-label={`${copy.noun} ${index + 1} time`}
                value={row.offsetMinutes}
                onChange={(event) => updateRow(row.key, { offsetMinutes: Number(event.currentTarget.value) })}
              >
                {settings.allowedOffsets.map((offset) => (
                  <option key={offset} value={offset} disabled={offset !== row.offsetMinutes && usedOffsets.includes(offset)}>
                    {formatReminderOffset(offset)}
                  </option>
                ))}
              </Form.Select>
              <span className='text-nowrap'>{copy.trailingCopy}</span>
            </span>
          )
          const removeButton = (
            <Tooltip title={removeDisabled ? copy.removeLastTooltip : undefined}>
              <CloseButton aria-label={`${copy.removeLabel} ${index + 1}`} disabled={removeDisabled} onClick={() => removeRow(row.key)} />
            </Tooltip>
          )
          return (
            <div
              key={row.key}
              className={classNames(styles.row, 'd-flex flex-column gap-2 px-3 py-2', {
                'border-top': index > 0,
                'zocks-bg-color-danger-background': noChannel,
              })}
            >
              <div className='d-flex align-items-start gap-3'>
                <span className={classNames(styles.line, 'd-flex align-items-center')}>
                  <span className={classNames(styles.dot, { [styles.dotError]: noChannel })} />
                </span>
                <span className={classNames(styles.line, 'd-flex flex-wrap align-items-center column-gap-3 row-gap-2 flex-grow-1 mw-0')}>
                  {offsetSelect}
                  {showChannels ? null : <RowTemplate row={row} templates={templates} />}
                </span>
                <span className={classNames(styles.line, 'd-flex align-items-center')}>{removeButton}</span>
              </div>
              {showChannels ? (
                <div className={classNames(styles.channelsIndent, 'd-flex align-items-center gap-3 flex-wrap')}>
                  <Form.Check
                    id={`reminder-${row.key}-email`}
                    className='mb-0'
                    label='Email'
                    aria-label={`Email ${index + 1}`}
                    checked={hasEmail}
                    onChange={(event) => toggleChannel(row, NotificationChannel.EMAIL, event.currentTarget.checked)}
                  />
                  {smsChannel === 'on' ? (
                    <Form.Check
                      id={`reminder-${row.key}-sms`}
                      className='mb-0'
                      label='Text message'
                      aria-label={`Text message ${index + 1}`}
                      checked={row.channels.includes(NotificationChannel.SMS)}
                      onChange={(event) => toggleChannel(row, NotificationChannel.SMS, event.currentTarget.checked)}
                    />
                  ) : null}
                  {hasEmail ? <RowTemplate row={row} templates={templates} /> : null}
                </div>
              ) : null}
              {noChannel ? (
                <span className={classNames(styles.channelsIndent, 'paragraph-small zocks-text-danger-dark')}>
                  {smsChannel === 'off'
                    ? 'Text messages are off for this account. Turn on Email to send this reminder.'
                    : 'Turn on Email, Text message or both to send this reminder.'}
                </span>
              ) : null}
              {!noChannel && !hasEmail ? (
                <Hint icon='bi-info-circle' className={classNames(styles.channelsIndent, 'zocks-text-gray-900')}>
                  {SMS_ONLY_HINT}
                </Hint>
              ) : null}
            </div>
          )
        })}
      </div>
      <div className='d-flex align-items-center gap-3'>
        <Button
          type='button'
          className={classNames('d-flex align-items-center zocks-gap-0.375 p-0 fw-semibold', styles.action, {
            'zocks-text-primary': draft.rows.length < MAX_REMINDER_ENTRIES,
          })}
          disabled={draft.rows.length >= MAX_REMINDER_ENTRIES}
          onClick={addRow}
        >
          <i aria-hidden='true' className='bi bi-plus' />
          {copy.addLabel}
        </Button>
        <span className='paragraph-small zocks-text-gray-600'>{`${draft.rows.length} of ${MAX_REMINDER_ENTRIES} added`}</span>
      </div>
    </div>
  )
}

export const ReminderCard: FC<ReminderCardProps> = ({
  copy,
  settings,
  draft,
  smsChannel,
  readOnly,
  editDisabled,
  editDisabledTooltip,
  isSaving,
  error,
  templates,
  onEdit,
  onCancel,
  onSave,
  onDraftChange,
  onShowTextMessages,
}) => {
  const dirty = draft != null && (settingsChanged(draft, settings) || hasTemplateChange(draft))
  const enabled = draft?.enabled ?? settings.enabled
  const textsOffWithSmsEntries = smsChannel === 'off' && hasSmsEntry(settings)

  return (
    <div className='d-flex flex-column gap-2'>
      <h6 className='mb-0'>{copy.title}</h6>
      <SettingsCard>
        <div className='p-4 d-flex flex-column gap-3'>
          <div className='d-flex align-items-start justify-content-between gap-3'>
            <div className='d-flex flex-column'>
              <span className='bold'>{copy.heading}</span>
              <span className='paragraph-small zocks-text-gray-600'>{copy.description}</span>
              <span className='paragraph-small zocks-text-gray-600'>
                Choose the email for each {copy.noun}. Write and edit emails in the{' '}
                <Link to={TEMPLATE_LIBRARY_PATH} className='zocks-text-primary text-decoration-underline'>
                  template library
                </Link>
                .
                {smsChannel === 'on' ? (
                  <>
                    {' '}
                    Texts use fixed wording, shown in{' '}
                    <Button type='button' className={LINK_CLASS} onClick={onShowTextMessages}>
                      Text messages
                    </Button>
                    .
                  </>
                ) : null}
              </span>
              {textsOffWithSmsEntries && draft == null ? (
                <Hint icon='bi-exclamation-circle' className='mt-2 zocks-text-gray-900'>
                  Texts are turned off, so reminders set to text don&apos;t go out by text. Update them here or turn texts back on in{' '}
                  <Button type='button' className={LINK_CLASS} onClick={onShowTextMessages}>
                    Text messages
                  </Button>
                  .
                </Hint>
              ) : null}
            </div>
            {draft == null ? (
              <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                <span className='paragraph-small zocks-text-gray-900'>{settings.enabled ? 'On' : 'Off'}</span>
                {readOnly ? (
                  <Tooltip title='This setting is managed at the account level'>
                    <i className='bi bi-info-circle' />
                  </Tooltip>
                ) : null}
              </div>
            ) : (
              <Form.Switch
                className='flex-shrink-0'
                aria-label={copy.heading}
                checked={draft.enabled}
                onChange={(event) => onDraftChange({ ...draft, enabled: event.currentTarget.checked })}
              />
            )}
          </div>
          {enabled && copy.banner != null ? (
            <Hint icon='bi-info-circle' className='rounded-2 px-3 py-2 zocks-bg-color-primary-light zocks-text-gray-900'>
              {copy.banner}
            </Hint>
          ) : null}
          {enabled && draft == null ? (
            <ReadOnlyRows
              copy={copy}
              settings={settings}
              showTextColumn={smsChannel === 'on' || textsOffWithSmsEntries}
              textsOff={textsOffWithSmsEntries}
              templates={templates}
            />
          ) : null}
          {enabled && draft != null ? (
            <EditableRows copy={copy} settings={settings} draft={draft} smsChannel={smsChannel} templates={templates} onDraftChange={onDraftChange} />
          ) : null}
          {enabled && smsChannel !== 'hidden' ? (
            <span className='paragraph-small zocks-text-gray-600'>
              Reminders go out within 30 minutes of the set time.{smsChannel === 'on' ? " Texts aren't sent during quiet hours." : null}
            </span>
          ) : null}
          {draft != null ? (
            <div className='d-flex align-items-end justify-content-between gap-3'>
              <span className='paragraph-small zocks-text-gray-600'>{draft.enabled && draft.rows.some((row) => row.id == null) ? NEW_ROW_CAVEAT : null}</span>
              <div className='d-flex gap-2'>
                <Button type='button' variant='secondaryGhostButton' onClick={onCancel}>
                  Cancel
                </Button>
                <Button type='button' variant='primary' disabled={!dirty || (draft.enabled && hasRowWithoutChannel(draft)) || isSaving} onClick={onSave}>
                  Save changes
                </Button>
              </div>
            </div>
          ) : null}
          {error != null ? <div className='paragraph-small text-danger'>{error}</div> : null}
          {draft == null && !readOnly ? (
            <Tooltip title={editDisabled ? editDisabledTooltip : undefined}>
              <Button
                type='button'
                className={classNames('d-flex align-items-center zocks-gap-0.375 p-0 fw-semibold align-self-start', styles.action, {
                  'zocks-text-primary': !editDisabled,
                })}
                disabled={editDisabled}
                onClick={onEdit}
              >
                <i aria-hidden='true' className='bi bi-pencil' />
                Edit
              </Button>
            </Tooltip>
          ) : null}
        </div>
      </SettingsCard>
    </div>
  )
}
